import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  Calculate as CalculateIcon,
  Send as SendIcon,
  Visibility as ViewIcon,
  GetApp as DownloadIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/authSlice";
import { useAuth } from "../../components/features/auth/AuthGuard";
import { ROLES, PERMISSIONS } from "../../constants";
import useNotification from "../../hooks/common/useNotification";
import useConfirmDialog from "../../hooks/common/useConfirmDialog";
import { LoadingSpinner } from "../../components/common/Loading";
import { payrollAPI } from "../../services/api/payroll.api";

// Tanzanian PAYE Tax Calculation (2024 rates)
const calculatePAYE = (salary) => {
  const monthlySalary = parseFloat(salary);

  // Annual salary
  const annualSalary = monthlySalary * 12;

  // Tax brackets (2024 Tanzania rates)
  if (annualSalary <= 360000) {
    return 0; // No tax for first 360,000 TZS annually
  } else if (annualSalary <= 1080000) {
    return ((annualSalary - 360000) * 0.08) / 12; // 8% for next 720,000
  } else if (annualSalary <= 3600000) {
    return ((annualSalary - 1080000) * 0.2 + 57600) / 12; // 20% for next 2,520,000
  } else if (annualSalary <= 6480000) {
    return ((annualSalary - 3600000) * 0.25 + 561600) / 12; // 25% for next 2,880,000
  } else {
    return ((annualSalary - 6480000) * 0.3 + 1281600) / 12; // 30% for amounts above 6,480,000
  }
};

// Calculate total deductions according to Tanzanian rules
const calculateTotalDeductions = (salary) => {
  const monthlySalary = parseFloat(salary);
  const nssf = monthlySalary * 0.1; // 10% NSSF
  const helsb = monthlySalary * 0.03; // 3% HELSB
  const bima = monthlySalary * 0.01; // 1% BIMA
  const paye = calculatePAYE(monthlySalary);

  return nssf + helsb + bima + paye;
};

const PayrollProcessingNew = () => {
  console.log("🚀 PayrollProcessingNew component mounted");

  const user = useSelector(selectUser);
  const { hasPermission, hasAnyRole } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { isOpen, config, openDialog, closeDialog, handleConfirm } =
    useConfirmDialog();

  console.log("👤 User:", user);
  console.log("🔐 Has View Payroll permission:", hasPermission("View Payroll"));
  console.log("👔 User roles:", user?.roles);

  // State management
  const [employees, setEmployees] = useState([]);
  const [payrollData, setPayrollData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState(new Date());
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [departments, setDepartments] = useState([]);
  const [payrollStatus, setPayrollStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  // Dialog states
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [rejectionDialog, setRejectionDialog] = useState(false);
  const [receiptDialog, setReceiptDialog] = useState(false);
  const [approvalComment, setApprovalComment] = useState("");
  const [rejectionComment, setRejectionComment] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    console.log(
      "📅 useEffect triggered - currentPeriod:",
      currentPeriod,
      "departmentFilter:",
      departmentFilter
    );
    fetchEmployeesForPayroll();
    fetchPayrollStatus();
  }, [currentPeriod, departmentFilter]);

  const fetchEmployeesForPayroll = async () => {
    try {
      setLoading(true);
      const month = currentPeriod.getMonth() + 1;
      const year = currentPeriod.getFullYear();

      const params = {
        month,
        year,
        department_id: departmentFilter || undefined,
        limit: 100,
      };

      console.log("🔍 Fetching payroll employees with params:", params);
      const response = await payrollAPI.getEmployeesForPayroll(params);
      console.log("📦 Response received:", response.data);

      if (response.data.success) {
        const employees = response.data.data.employees || [];
        console.log("✅ Employees fetched:", employees.length, employees);
        setEmployees(employees);

        // Initialize payroll data with employee details
        const initialPayrollData = employees.map((emp) => ({
          employee_id: emp.id,
          employee_name: emp.name,
          registration_number: emp.registration_number,
          department: emp.department,
          designation: emp.designation,
          salary: parseFloat(emp.salary) || 0,
          bank_name: emp.bank_name,
          account_number: emp.account_number,
          helsb: emp.helsb,
          nssf: emp.nssf,
          bima: emp.bima,
          working_days: parseInt(emp.working_days) || 0,
          total_hours: parseFloat(emp.total_hours) || 0,
          net_pay:
            parseFloat(emp.salary) - calculateTotalDeductions(emp.salary) || 0, // Calculate net pay after Tanzanian deductions
          status: "pending",
        }));

        console.log("💰 Payroll data initialized:", initialPayrollData);
        setPayrollData(initialPayrollData);
      } else {
        throw new Error(response.data.message || "Failed to fetch employees");
      }
    } catch (error) {
      console.error("❌ Failed to fetch employees for payroll:", error);
      console.error("Error details:", error.response?.data || error.message);
      showError(
        "Failed to fetch employees for payroll: " +
          (error.response?.data?.message || error.message)
      );
      setEmployees([]);
      setPayrollData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayrollStatus = async () => {
    try {
      setStatusLoading(true);
      const month = currentPeriod.getMonth() + 1;
      const year = currentPeriod.getFullYear();

      console.log("🔍 Fetching payroll status for:", { month, year });
      const response = await payrollAPI.getPayrollStatus({ month, year });
      console.log("📊 Status response:", response.data);

      if (response.data.success) {
        console.log("✅ Payroll status set:", response.data.data);
        setPayrollStatus(response.data.data);
      }
    } catch (error) {
      console.error("❌ Failed to fetch payroll status:", error);
      console.error("Error details:", error.response?.data || error.message);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleNetPayChange = (employeeId, value) => {
    setPayrollData((prev) =>
      prev.map((item) =>
        item.employee_id === employeeId
          ? { ...item, net_pay: parseFloat(value) || 0 }
          : item
      )
    );
  };

  const handleProcessPayroll = async () => {
    try {
      setProcessing(true);

      const month = currentPeriod.getMonth() + 1;
      const year = currentPeriod.getFullYear();

      const processData = {
        month,
        year,
        employees: payrollData.map((item) => ({
          employee_id: item.employee_id,
          net_pay: item.net_pay,
        })),
      };

      const response = await payrollAPI.processPayroll(processData);

      if (response.data.success) {
        showSuccess("Payroll processed successfully");
        await fetchPayrollStatus();
        // Update status to processed
        setPayrollData((prev) =>
          prev.map((item) => ({ ...item, status: "processed" }))
        );
      } else {
        throw new Error(response.data.message || "Failed to process payroll");
      }
    } catch (error) {
      console.error("Failed to process payroll:", error);
      showError("Failed to process payroll");
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      setSubmitting(true);

      const month = currentPeriod.getMonth() + 1;
      const year = currentPeriod.getFullYear();

      const submitData = { month, year };

      const response = await payrollAPI.submitPayrollForApproval(submitData);

      if (response.data.success) {
        showSuccess("Payroll submitted for approval successfully");
        await fetchPayrollStatus();
        // Update status to submitted
        setPayrollData((prev) =>
          prev.map((item) => ({ ...item, status: "submitted" }))
        );
      } else {
        throw new Error(
          response.data.message || "Failed to submit payroll for approval"
        );
      }
    } catch (error) {
      console.error("Failed to submit payroll for approval:", error);
      showError("Failed to submit payroll for approval");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprovePayroll = async () => {
    try {
      const month = currentPeriod.getMonth() + 1;
      const year = currentPeriod.getFullYear();

      const approvalData = {
        month,
        year,
        comment: approvalComment,
      };

      const response = await payrollAPI.approvePayroll(approvalData);

      if (response.data.success) {
        showSuccess("Payroll approved successfully");
        setApprovalDialog(false);
        setApprovalComment("");
        await fetchPayrollStatus();
        // Update status to approved
        setPayrollData((prev) =>
          prev.map((item) => ({ ...item, status: "approved" }))
        );
      } else {
        throw new Error(response.data.message || "Failed to approve payroll");
      }
    } catch (error) {
      console.error("Failed to approve payroll:", error);
      showError("Failed to approve payroll");
    }
  };

  const handleRejectPayroll = async () => {
    if (!rejectionComment.trim()) {
      showError("Rejection comment is required");
      return;
    }

    try {
      const month = currentPeriod.getMonth() + 1;
      const year = currentPeriod.getFullYear();

      const rejectionData = {
        month,
        year,
        comment: rejectionComment,
      };

      const response = await payrollAPI.rejectPayroll(rejectionData);

      if (response.data.success) {
        showSuccess("Payroll rejected successfully");
        setRejectionDialog(false);
        setRejectionComment("");
        await fetchPayrollStatus();
        // Update status to rejected
        setPayrollData((prev) =>
          prev.map((item) => ({ ...item, status: "rejected" }))
        );
      } else {
        throw new Error(response.data.message || "Failed to reject payroll");
      }
    } catch (error) {
      console.error("Failed to reject payroll:", error);
      showError("Failed to reject payroll");
    }
  };

  const handleDownloadReport = () => {
    const month = currentPeriod.getMonth() + 1;
    const year = currentPeriod.getFullYear();
    const period = format(currentPeriod, "MMMM yyyy");

    // Create CSV content
    const csvHeaders = [
      "Employee Name",
      "Registration Number",
      "Department",
      "Designation",
      "Basic Salary",
      "Working Days",
      "Total Hours",
      "NSSF (10%)",
      "HELSB (3%)",
      "BIMA (1%)",
      "PAYE Tax",
      "Total Deductions",
      "Net Pay",
      "Bank Name",
      "Account Number",
      "Status",
    ];

    const csvData = payrollData.map((item) => [
      item.employee_name,
      item.registration_number || "N/A",
      item.department || "N/A",
      item.designation || "N/A",
      item.salary,
      item.working_days,
      item.total_hours,
      item.salary * 0.1,
      item.salary * 0.03,
      item.salary * 0.01,
      calculatePAYE(item.salary),
      calculateTotalDeductions(item.salary),
      item.net_pay,
      item.bank_name || "N/A",
      item.account_number || "N/A",
      item.status,
    ]);

    // Add total row
    const totalNetPay = payrollData.reduce(
      (sum, item) => sum + (parseFloat(item.net_pay) || 0),
      0
    );
    const totalSalary = payrollData.reduce(
      (sum, item) => sum + (parseFloat(item.salary) || 0),
      0
    );
    const totalDeductions = payrollData.reduce(
      (sum, item) => sum + calculateTotalDeductions(item.salary),
      0
    );

    csvData.push([
      "TOTAL",
      "",
      "",
      "",
      totalSalary,
      "",
      "",
      totalSalary * 0.1,
      totalSalary * 0.03,
      totalSalary * 0.01,
      payrollData.reduce((sum, item) => sum + calculatePAYE(item.salary), 0),
      totalDeductions,
      totalNetPay,
      "",
      "",
      "",
    ]);

    // Convert to CSV string
    const csvContent = [
      csvHeaders.join(","),
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `payroll_report_${period.replace(" ", "_")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showSuccess(`Payroll report for ${period} downloaded successfully`);
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat("en-TZ", {
      style: "currency",
      currency: "TZS",
      minimumFractionDigits: 0,
    }).format(numAmount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "processed":
        return "success";
      case "submitted":
        return "warning";
      case "approved":
        return "primary";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "processed":
        return "Processed";
      case "submitted":
        return "Submitted for Approval";
      case "approved":
        return "Approved";
      case "rejected":
        return "Rejected";
      default:
        return "Pending";
    }
  };

  const canProcess =
    hasAnyRole([ROLES.ADMIN, ROLES.HR_MANAGER, ROLES.FINANCE_MANAGER]) &&
    payrollData.length > 0 &&
    payrollData.every((item) => item.status === "pending");

  const canSubmit =
    hasAnyRole([ROLES.ADMIN, ROLES.HR_MANAGER, ROLES.FINANCE_MANAGER]) &&
    payrollData.length > 0 &&
    payrollData.every((item) => item.status === "processed");

  const canApprove =
    hasAnyRole([ROLES.ADMIN]) &&
    payrollData.length > 0 &&
    payrollData.every((item) => item.status === "submitted");

  const canReject =
    hasAnyRole([ROLES.ADMIN]) &&
    payrollData.length > 0 &&
    payrollData.every((item) => item.status === "submitted");

  const totalNetPay = payrollData.reduce(
    (sum, item) => sum + (parseFloat(item.net_pay) || 0),
    0
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
            Payroll Processing
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Process payroll for employees with approved timesheets
          </Typography>
        </Box>

        {/* Period Selection and Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Pay Period"
                  value={currentPeriod}
                  onChange={setCurrentPeriod}
                  views={["year", "month"]}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Department"
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.department_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchEmployeesForPayroll}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="h6" color="primary">
                  Total Net Pay: {formatCurrency(totalNetPay)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Payroll Status Summary */}
        {payrollStatus && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Payroll Status for {format(currentPeriod, "MMMM yyyy")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {payrollStatus.summary?.total_employees || 0}
                    </Typography>
                    <Typography variant="body2">Total Employees</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      {payrollStatus.summary?.processed || 0}
                    </Typography>
                    <Typography variant="body2">Processed</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="warning.main">
                      {payrollStatus.summary?.submitted || 0}
                    </Typography>
                    <Typography variant="body2">Submitted</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary.main">
                      {payrollStatus.summary?.approved || 0}
                    </Typography>
                    <Typography variant="body2">Approved</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="error.main">
                      {payrollStatus.summary?.rejected || 0}
                    </Typography>
                    <Typography variant="body2">Rejected</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="info.main">
                      {formatCurrency(
                        payrollStatus.summary?.total_net_pay || 0
                      )}
                    </Typography>
                    <Typography variant="body2">Total Net Pay</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Only employees with supervisor-approved
            timesheets are included in payroll processing. Net pay amounts are
            automatically calculated using Tanzanian statutory deductions (NSSF
            10%, HELSB 3%, BIMA 1%, and PAYE tax). Finance managers can adjust
            net pay amounts and submit for admin approval.
          </Typography>
        </Alert>

        {/* Payroll Table */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ p: 3 }}>
                <LoadingSpinner />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Employee</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Details</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Bank Info</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Working Days</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Salary</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Net Pay</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Status</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payrollData.map((item) => (
                      <TableRow
                        key={item.employee_id}
                        onClick={() => {
                          setSelectedEmployee(item);
                          setReceiptDialog(true);
                        }}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "action.hover" },
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: "medium" }}
                            >
                              {item.employee_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.registration_number} • {item.designation}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              <strong>HELSB:</strong> {item.helsb || "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>NSSF:</strong> {item.nssf || "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>BIMA:</strong> {item.bima || "N/A"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              <strong>Bank:</strong> {item.bank_name || "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Account:</strong>{" "}
                              {item.account_number || "N/A"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {item.working_days} days
                            </Typography>
                            <Typography variant="body2">
                              {item.total_hours}h
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{formatCurrency(item.salary)}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.net_pay}
                            onChange={(e) =>
                              handleNetPayChange(
                                item.employee_id,
                                e.target.value
                              )
                            }
                            disabled={item.status !== "pending"}
                            InputProps={{
                              startAdornment: (
                                <Typography variant="body2">TZS</Typography>
                              ),
                            }}
                            sx={{ width: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusText(item.status)}
                            color={getStatusColor(item.status)}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {canProcess && (
                <LoadingButton
                  variant="contained"
                  startIcon={<CalculateIcon />}
                  loading={processing}
                  onClick={handleProcessPayroll}
                >
                  Process Payroll
                </LoadingButton>
              )}

              {canSubmit && (
                <LoadingButton
                  variant="contained"
                  color="warning"
                  startIcon={<SendIcon />}
                  loading={submitting}
                  onClick={handleSubmitForApproval}
                >
                  Submit for Approval
                </LoadingButton>
              )}

              {canApprove && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={() => setApprovalDialog(true)}
                >
                  Approve Payroll
                </Button>
              )}

              {canReject && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<RejectIcon />}
                  onClick={() => setRejectionDialog(true)}
                >
                  Reject Payroll
                </Button>
              )}

              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadReport}
                disabled={payrollData.length === 0}
              >
                Download Report
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Approval Dialog */}
        <Dialog
          open={approvalDialog}
          onClose={() => setApprovalDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Approve Payroll</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to approve payroll for{" "}
                <strong>{format(currentPeriod, "MMMM yyyy")}</strong>?
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Approval Comment (Optional)"
                value={approvalComment}
                onChange={(e) => setApprovalComment(e.target.value)}
                sx={{ mt: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
            <Button
              onClick={handleApprovePayroll}
              variant="contained"
              color="success"
              startIcon={<ApproveIcon />}
            >
              Approve
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rejection Dialog */}
        <Dialog
          open={rejectionDialog}
          onClose={() => setRejectionDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reject Payroll</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to reject payroll for{" "}
                <strong>{format(currentPeriod, "MMMM yyyy")}</strong>?
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Rejection comment is required.
              </Alert>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Rejection Comment (Required)"
                value={rejectionComment}
                onChange={(e) => setRejectionComment(e.target.value)}
                required
                error={!rejectionComment.trim()}
                helperText={
                  !rejectionComment.trim()
                    ? "Rejection comment is required"
                    : ""
                }
                sx={{ mt: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectionDialog(false)}>Cancel</Button>
            <Button
              onClick={handleRejectPayroll}
              variant="contained"
              color="error"
              startIcon={<RejectIcon />}
              disabled={!rejectionComment.trim()}
            >
              Reject
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payroll Receipt Dialog */}
        <Dialog
          open={receiptDialog}
          onClose={() => setReceiptDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">Payroll Receipt</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedEmployee && format(currentPeriod, "MMMM yyyy")}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedEmployee && (
              <Box sx={{ pt: 2 }}>
                {/* Header */}
                <Box
                  sx={{
                    textAlign: "center",
                    mb: 3,
                    pb: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                    GHF HR Management System
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Payroll Receipt
                  </Typography>
                </Box>

                {/* Employee Details */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Employee Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {selectedEmployee.employee_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Registration Number
                    </Typography>
                    <Typography variant="body1">
                      {selectedEmployee.registration_number || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Department
                    </Typography>
                    <Typography variant="body1">
                      {selectedEmployee.department || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Pay Period
                    </Typography>
                    <Typography variant="body1">
                      {format(currentPeriod, "MMMM yyyy")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Working Days
                    </Typography>
                    <Typography variant="body1">
                      {selectedEmployee.working_days} days (
                      {selectedEmployee.total_hours} hours)
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Designation
                    </Typography>
                    <Typography variant="body1">
                      {selectedEmployee.designation || "N/A"}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Tanzanian Payroll Calculations */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Payroll Calculations (Tanzanian Rules)
                  </Typography>

                  {/* Basic Salary */}
                  <Box
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Basic Salary
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(selectedEmployee.salary)}
                    </Typography>
                  </Box>

                  {/* Deductions */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mb: 2 }}
                    >
                      Statutory Deductions
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1,
                            borderBottom: 1,
                            borderColor: "divider",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            NSSF (10%)
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "medium" }}
                          >
                            {formatCurrency(selectedEmployee.salary * 0.1)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1,
                            borderBottom: 1,
                            borderColor: "divider",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            HELSB (3%)
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "medium" }}
                          >
                            {formatCurrency(selectedEmployee.salary * 0.03)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1,
                            borderBottom: 1,
                            borderColor: "divider",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            BIMA (1%)
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "medium" }}
                          >
                            {formatCurrency(selectedEmployee.salary * 0.01)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1,
                            borderBottom: 1,
                            borderColor: "divider",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            PAYE Tax
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "medium" }}
                          >
                            {formatCurrency(
                              calculatePAYE(selectedEmployee.salary)
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Total Deductions */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 2,
                      borderTop: 2,
                      borderColor: "divider",
                      mt: 2,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      Total Deductions
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {formatCurrency(
                        calculateTotalDeductions(selectedEmployee.salary)
                      )}
                    </Typography>
                  </Box>

                  {/* Net Pay */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 2,
                      borderTop: 2,
                      borderColor: "divider",
                      bgcolor: "grey.50",
                      borderRadius: 1,
                      px: 2,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      Net Pay (After Deductions)
                    </Typography>
                    <Typography variant="h5" color="primary.main">
                      {formatCurrency(
                        selectedEmployee.salary -
                          calculateTotalDeductions(selectedEmployee.salary)
                      )}
                    </Typography>
                  </Box>
                </Box>

                {/* Bank Details */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Bank Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Bank Name
                      </Typography>
                      <Typography variant="body1">
                        {selectedEmployee.bank_name || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Account Number
                      </Typography>
                      <Typography variant="body1">
                        {selectedEmployee.account_number || "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Footer */}
                <Box
                  sx={{
                    textAlign: "center",
                    mt: 4,
                    pt: 2,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    This is a computer-generated document. No signature
                    required.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Generated on {format(new Date(), "PPP")}
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReceiptDialog(false)}>Close</Button>
            <Button
              variant="contained"
              onClick={() => {
                window.print();
              }}
            >
              Print Receipt
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PayrollProcessingNew;
